import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { CognitoUser, CognitoUserPool, CognitoUserAttribute, AuthenticationDetails } from 'amazon-cognito-identity-js';

// Types for authentication context
interface User {
  email: string;
  givenName?: string;
  familyName?: string;
  fontSize?: string;
  highContrast?: boolean;
  preferredContactMethod?: string;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  signUp: (email: string, password: string, givenName?: string, familyName?: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  confirmSignUp: (email: string, code: string) => Promise<void>;
  resendConfirmationCode: (email: string) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  confirmPassword: (email: string, code: string, newPassword: string) => Promise<void>;
}

// Cognito configuration - these will be populated from CDK outputs
const poolData = {
  UserPoolId: process.env.REACT_APP_USER_POOL_ID || '',
  ClientId: process.env.REACT_APP_USER_POOL_CLIENT_ID || '',
};

// Lazy initialization of user pool to avoid errors when config is missing
let userPool: CognitoUserPool | null = null;

const getUserPool = (): CognitoUserPool => {
  if (!userPool && poolData.UserPoolId && poolData.ClientId) {
    userPool = new CognitoUserPool(poolData);
  }
  if (!userPool) {
    throw new Error('Cognito User Pool is not configured');
  }
  return userPool;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check for existing session on mount
  useEffect(() => {
    checkAuthState();
  }, []);

  const checkAuthState = async () => {
    try {
      // Check if AWS configuration is available
      if (!poolData.UserPoolId || !poolData.ClientId) {
        console.warn('AWS Cognito not configured - running in development mode');
        setIsLoading(false);
        return;
      }

      const currentUser = getUserPool().getCurrentUser();
      if (currentUser) {
        currentUser.getSession((err: any, session: any) => {
          if (err) {
            console.error('Session error:', err);
            setIsLoading(false);
            return;
          }
          
          if (session && session.isValid()) {
            // Get user attributes
            currentUser.getUserAttributes((err, attributes) => {
              if (err) {
                console.error('Error getting user attributes:', err);
                setIsLoading(false);
                return;
              }

              const userInfo: User = {
                email: '',
              };

              attributes?.forEach((attribute) => {
                switch (attribute.getName()) {
                  case 'email':
                    userInfo.email = attribute.getValue();
                    break;
                  case 'given_name':
                    userInfo.givenName = attribute.getValue();
                    break;
                  case 'family_name':
                    userInfo.familyName = attribute.getValue();
                    break;
                  case 'custom:fontSize':
                    userInfo.fontSize = attribute.getValue();
                    break;
                  case 'custom:highContrast':
                    userInfo.highContrast = attribute.getValue() === 'true';
                    break;
                  case 'custom:contactMethod':
                    userInfo.preferredContactMethod = attribute.getValue();
                    break;
                }
              });

              setUser(userInfo);
              setIsAuthenticated(true);
              setIsLoading(false);
            });
          } else {
            setIsLoading(false);
          }
        });
      } else {
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Auth check error:', error);
      setIsLoading(false);
    }
  };

  const signUp = async (email: string, password: string, givenName?: string, familyName?: string): Promise<void> => {
    if (!poolData.UserPoolId || !poolData.ClientId) {
      throw new Error('Authentication service is not configured. Please ensure the backend is deployed.');
    }

    return new Promise((resolve, reject) => {
      const attributeList: CognitoUserAttribute[] = [];
      
      // Add email attribute
      attributeList.push(new CognitoUserAttribute({
        Name: 'email',
        Value: email,
      }));

      // Add optional attributes
      if (givenName) {
        attributeList.push(new CognitoUserAttribute({
          Name: 'given_name',
          Value: givenName,
        }));
      }

      if (familyName) {
        attributeList.push(new CognitoUserAttribute({
          Name: 'family_name',
          Value: familyName,
        }));
      }

      // Set default elderly-friendly preferences
      attributeList.push(new CognitoUserAttribute({
        Name: 'custom:fontSize',
        Value: 'large',
      }));

      attributeList.push(new CognitoUserAttribute({
        Name: 'custom:highContrast',
        Value: 'false',
      }));

      attributeList.push(new CognitoUserAttribute({
        Name: 'custom:contactMethod',
        Value: 'email',
      }));

      getUserPool().signUp(email, password, attributeList, [], (err, result) => {
        if (err) {
          reject(err);
          return;
        }
        resolve();
      });
    });
  };

  const signIn = async (email: string, password: string): Promise<void> => {
    if (!poolData.UserPoolId || !poolData.ClientId) {
      throw new Error('Authentication service is not configured. Please ensure the backend is deployed.');
    }

    return new Promise((resolve, reject) => {
      const authenticationDetails = new AuthenticationDetails({
        Username: email,
        Password: password,
      });

      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: getUserPool(),
      });

      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: (session) => {
          // Get user attributes after successful login
          cognitoUser.getUserAttributes((err, attributes) => {
            if (err) {
              reject(err);
              return;
            }

            const userInfo: User = {
              email: '',
            };

            attributes?.forEach((attribute) => {
              switch (attribute.getName()) {
                case 'email':
                  userInfo.email = attribute.getValue();
                  break;
                case 'given_name':
                  userInfo.givenName = attribute.getValue();
                  break;
                case 'family_name':
                  userInfo.familyName = attribute.getValue();
                  break;
                case 'custom:fontSize':
                  userInfo.fontSize = attribute.getValue();
                  break;
                case 'custom:highContrast':
                  userInfo.highContrast = attribute.getValue() === 'true';
                  break;
                case 'custom:contactMethod':
                  userInfo.preferredContactMethod = attribute.getValue();
                  break;
              }
            });

            setUser(userInfo);
            setIsAuthenticated(true);
            resolve();
          });
        },
        onFailure: (err) => {
          reject(err);
        },
        newPasswordRequired: (userAttributes, requiredAttributes) => {
          // Handle new password required scenario
          reject(new Error('New password required'));
        },
      });
    });
  };

  const signOut = async (): Promise<void> => {
    return new Promise((resolve) => {
      try {
        const currentUser = getUserPool().getCurrentUser();
        if (currentUser) {
          currentUser.signOut();
        }
      } catch (error) {
        console.warn('Error during sign out:', error);
      }
      setUser(null);
      setIsAuthenticated(false);
      resolve();
    });
  };

  const confirmSignUp = async (email: string, code: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: getUserPool(),
      });

      cognitoUser.confirmRegistration(code, true, (err, result) => {
        if (err) {
          reject(err);
          return;
        }
        resolve();
      });
    });
  };

  const resendConfirmationCode = async (email: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: getUserPool(),
      });

      cognitoUser.resendConfirmationCode((err, result) => {
        if (err) {
          reject(err);
          return;
        }
        resolve();
      });
    });
  };

  const forgotPassword = async (email: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: getUserPool(),
      });

      cognitoUser.forgotPassword({
        onSuccess: () => {
          resolve();
        },
        onFailure: (err) => {
          reject(err);
        },
      });
    });
  };

  const confirmPassword = async (email: string, code: string, newPassword: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: getUserPool(),
      });

      cognitoUser.confirmPassword(code, newPassword, {
        onSuccess: () => {
          resolve();
        },
        onFailure: (err) => {
          reject(err);
        },
      });
    });
  };

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated,
    signUp,
    signIn,
    signOut,
    confirmSignUp,
    resendConfirmationCode,
    forgotPassword,
    confirmPassword,
  };

  // Show loading screen while checking authentication
  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '18px',
        color: '#666'
      }}>
        <div style={{ marginBottom: '16px' }}>Loading My Nerdy Son...</div>
        <div style={{
          width: '40px',
          height: '40px',
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #007bff',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};